import { Link } from "react-router-dom";
import { Award, Heart, Users, Gem } from "lucide-react";

const ASSETS = {
  interior: "https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/6v18lvny_IMG_2636.jpeg",
  craftsmanship: "https://customer-assets.emergentagent.com/job_lunettes-sur-mesure/artifacts/9cyci0cc_IMG_1738.jpeg",
};

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#F9F9F7]">
        <div className="container-premium">
          <div className="max-w-3xl">
            <p className="caption text-[#D4AF37] mb-6">Notre Histoire</p>
            <h1 className="font-serif text-5xl md:text-7xl font-medium tracking-tight leading-[1.1] mb-8 text-[#1A1A1A]">
              À Propos
            </h1>
            <p className="text-xl font-light text-[#666660] leading-relaxed">
              L'Opticien de Vaires est née d'une passion pour l'excellence visuelle 
              et l'accompagnement personnalisé. Notre boutique indépendante 
              élève les standards de l'optique à Vaires-sur-Marne.
            </p>
          </div>
        </div>
      </section>

      {/* Main Image */}
      <section className="bg-[#F9F9F7]">
        <div className="container-premium">
          <div className="image-zoom">
            <img
              src={ASSETS.interior}
              alt="Notre boutique"
              className="w-full h-[50vh] md:h-[70vh] object-cover"
              data-testid="about-main-image"
            />
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-padding bg-[#F9F9F7]">
        <div className="container-premium">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="caption text-[#D4AF37] mb-6">Notre Philosophie</p>
              <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight mb-8 text-[#1A1A1A]">
                L'écoute au cœur de notre métier
              </h2>
              <div className="divider-gold mb-8" />
              <div className="space-y-6 text-[#666660] font-light leading-relaxed">
                <p>
                  Nous croyons que chaque personne mérite un accompagnement 
                  personnalisé dans le choix de ses lunettes. C'est pourquoi 
                  nous prenons le temps de comprendre vos besoins, votre 
                  style de vie et vos aspirations esthétiques.
                </p>
                <p>
                  Notre expertise en conseil visagiste nous permet de vous 
                  guider vers la monture qui révèlera le meilleur de votre 
                  personnalité, tout en répondant parfaitement à vos 
                  exigences visuelles.
                </p>
                <p>
                  Spécialistes reconnus des montures percées, nous maîtrisons 
                  l'art de créer des lunettes légères et élégantes qui offrent 
                  un champ de vision sans limite.
                </p>
              </div>
            </div>
            <div className="image-zoom">
              <img
                src={ASSETS.craftsmanship}
                alt="Notre savoir-faire"
                className="w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-[#F0F0EB]">
        <div className="container-premium">
          <div className="text-center mb-16">
            <p className="caption text-[#D4AF37] mb-4">Nos Valeurs</p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-[#1A1A1A]">
              Ce qui nous anime
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div data-testid="value-elegance" className="text-center p-8">
              <div className="w-16 h-16 mx-auto flex items-center justify-center border border-[#D4AF37] mb-6">
                <Gem size={28} className="text-[#D4AF37]" strokeWidth={1} />
              </div>
              <h3 className="font-serif text-xl mb-4 text-[#1A1A1A]">Élégance</h3>
              <p className="text-[#666660] font-light text-sm leading-relaxed">
                Une sélection de montures raffinées pour sublimer chaque regard
              </p>
            </div>

            <div data-testid="value-personalization" className="text-center p-8">
              <div className="w-16 h-16 mx-auto flex items-center justify-center border border-[#D4AF37] mb-6">
                <Heart size={28} className="text-[#D4AF37]" strokeWidth={1} />
              </div>
              <h3 className="font-serif text-xl mb-4 text-[#1A1A1A]">Personnalisation</h3>
              <p className="text-[#666660] font-light text-sm leading-relaxed">
                Chaque paire est unique, conçue selon vos désirs et besoins
              </p>
            </div>

            <div data-testid="value-listening" className="text-center p-8">
              <div className="w-16 h-16 mx-auto flex items-center justify-center border border-[#D4AF37] mb-6">
                <Users size={28} className="text-[#D4AF37]" strokeWidth={1} />
              </div>
              <h3 className="font-serif text-xl mb-4 text-[#1A1A1A]">Écoute</h3>
              <p className="text-[#666660] font-light text-sm leading-relaxed">
                Nous prenons le temps de comprendre vos attentes
              </p>
            </div>

            <div data-testid="value-expertise" className="text-center p-8">
              <div className="w-16 h-16 mx-auto flex items-center justify-center border border-[#D4AF37] mb-6">
                <Award size={28} className="text-[#D4AF37]" strokeWidth={1} />
              </div>
              <h3 className="font-serif text-xl mb-4 text-[#1A1A1A]">Expertise</h3>
              <p className="text-[#666660] font-light text-sm leading-relaxed">
                Un savoir-faire reconnu en montures percées et verres premium
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Rimless Specialty */}
      <section className="section-padding bg-[#1A1A1A]">
        <div className="container-premium">
          <div className="max-w-3xl mx-auto text-center">
            <p className="caption text-[#D4AF37] mb-6">Notre Spécialité</p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight mb-8 text-white">
              Montures Percées
            </h2>
            <div className="w-16 h-[1px] bg-[#D4AF37] mx-auto mb-8" />
            <p className="text-lg font-light text-white/80 leading-relaxed mb-8">
              Les montures percées représentent l'apogée de l'élégance optique. 
              Sans cadre pour entraver le regard, elles offrent une légèreté 
              incomparable et un style raffiné. Notre expertise technique nous 
              permet de réaliser ces créations délicates avec une précision 
              millimétrée.
            </p>
            <Link
              to="/personnalisation"
              data-testid="about-cta-customization"
              className="btn-primary bg-[#D4AF37] hover:bg-[#B8962F]"
            >
              Découvrir nos réalisations
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#F9F9F7]">
        <div className="container-premium">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 p-12 border border-[#E5E5E0]">
            <div>
              <h3 className="font-serif text-3xl mb-4 text-[#1A1A1A]">
                Venez nous rencontrer
              </h3>
              <p className="text-[#666660] font-light">
                Découvrez notre boutique et vivez l'expérience Optique Vaires
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="about-cta-rdv"
              className="btn-primary whitespace-nowrap"
            >
              Prendre Rendez-vous
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
